import type { NMPLocale } from "@borealis/locale-common";
import { formatNumber } from "@borealis/number-format";
import React from "react";

/**
 * Core input mask hook that requires locale to be passed explicitly.
 * Use this in contexts where AppConfigProvider is not available (e.g., islands).
 */
export function useInputMaskWithLocale(value: string, locale: NMPLocale) {
	const inputRef = React.useRef<HTMLInputElement>(null);

	// Ensure empty string remains empty and does not convert to 0
	const formatted = React.useMemo(() => {
		if (value === "") return "";
		const parsed = Number(value);
		if (!Number.isFinite(parsed)) return "";
		try {
			return formatNumber(parsed, locale);
		} catch {
			// Fallback to unformatted number if formatNumber fails
			return String(parsed);
		}
	}, [value, locale]);

	const [maskedValue, setMaskedValue] = React.useState(formatted);
	const [unmaskedValue, setUnmaskedValue] = React.useState(value);

	React.useEffect(() => {
		setMaskedValue(formatted);
	}, [formatted]);

	React.useEffect(() => {
		if (inputRef.current) {
			inputRef.current.value = maskedValue;
		}
	}, [maskedValue]);

	// Remove spaces, dots, and commas from maskedValue to get unmaskedValue
	React.useEffect(() => {
		setUnmaskedValue(maskedValue.replace(/[\s.,]/g, ""));
	}, [maskedValue]);

	return {
		inputRef,
		maskedValue,
		setMaskedValue,
		unmaskedValue,
	};
}
